/**
 * Environment configuration for admin dashboard
 * Supports switching between production, staging, and dev environments
 */

export type Environment = 'production' | 'staging' | 'dev';

export interface EnvironmentConfig {
  name: string;
  apiBaseUrl: string;
  apiVersion?: string;
  wsUrl: string;
  displayName: string;
  badgeColor: string;
}

export const ENVIRONMENTS: Record<Environment, EnvironmentConfig> = {
  production: {
    name: 'production',
    apiBaseUrl: 'https://polarride-api-17dr.vercel.app',
    apiVersion: 'v2',
    wsUrl: 'wss://polarride-api-17dr.vercel.app',
    displayName: 'Production',
    badgeColor: '#10b981', // green
  },
  staging: {
    name: 'staging',
    apiBaseUrl: 'https://polarride-staging-api.vercel.app',
    apiVersion: 'v2',
    wsUrl: 'wss://polarride-staging-api.vercel.app',
    displayName: 'Staging',
    badgeColor: '#f59e0b', // orange
  },
  dev: {
    name: 'dev',
    apiBaseUrl: 'http://localhost:5001',
    apiVersion: 'v2',
    wsUrl: 'ws://localhost:5001',
    displayName: 'Dev',
    badgeColor: '#8b5cf6', // purple
  },
};

/**
 * Get current environment based on hostname (no manual switching)
 * This ensures each deployment URL always uses the correct API
 */
export const getCurrentEnvironment = (): Environment => {
  if (typeof window === 'undefined') {
    return 'production'; // SSR fallback
  }

  const hostname = window.location.hostname;

  // Staging: Check staging FIRST to prevent production catch-all from matching
  if (
    hostname === 'admin-staging.polarride.com' ||
    hostname.includes('polarride-admin-staging') ||
    hostname.includes('polarride-frontend-staging')
  ) {
    return 'staging';
  }

  // Production: Explicit domains only
  if (
    hostname === 'admin.polarride.com' ||
    hostname === 'prmc.polarride.com' ||
    hostname === 'polarride-admin.vercel.app' ||
    hostname === 'polarride-frontend.vercel.app' ||
    // Match only official Vercel deployment URLs for main branch (not staging)
    (hostname.endsWith('.vercel.app') &&
     hostname.startsWith('polarride-frontend-') &&
     !hostname.includes('staging'))
  ) {
    return 'production';
  }

  // Dev: localhost or 127.0.0.1
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'dev';
  }

  // Default fallback for any other domains
  return 'production';
};

/**
 * @deprecated Manual environment switching is disabled.
 * Environment is now automatically detected from hostname.
 * Use separate deployment URLs for different environments.
 */
export const setCurrentEnvironment = (env: Environment): void => {
};

/**
 * Get configuration for current environment
 */
export const getEnvironmentConfig = (): EnvironmentConfig => {
  const env = getCurrentEnvironment();
  return ENVIRONMENTS[env];
};

/**
 * Get API base URL for current environment
 */
export const getApiBaseUrl = (): string => {
  return getEnvironmentConfig().apiBaseUrl;
};

export const getApiVersion = (): string => {
  return getEnvironmentConfig().apiVersion ?? 'v1';
};

/**
 * Get WebSocket URL for current environment
 */
export const getWsUrl = (): string => {
  return getEnvironmentConfig().wsUrl;
};
